import React, { useState } from 'react';
import { ShopMembership, MembershipRole } from '../types';

interface EditMemberRolesModalProps {
  member: ShopMembership;
  onClose: () => void;
  onRolesUpdated: (membershipId: string, newRoles: MembershipRole[]) => void;
}

const EditMemberRolesModal: React.FC<EditMemberRolesModalProps> = ({
  member,
  onClose,
  onRolesUpdated
}) => {
  const [selectedRoles, setSelectedRoles] = useState<MembershipRole[]>(member.roles);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const availableRoles: { role: MembershipRole; label: string; description: string }[] = [
    {
      role: 'ADMIN',
      label: 'Administrator',
      description: 'Can manage shop settings and members'
    },
    {
      role: 'MEMBER',
      label: 'Member',
      description: 'Can create and edit slides'
    },
    {
      role: 'VIEWER',
      label: 'Viewer',
      description: 'Can view slides but not edit'
    },
    {
      role: 'CLIENT',
      label: 'Client',
      description: 'Can view curated feed cubes shared to them'
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (selectedRoles.length === 0) {
      setError('Please select at least one role');
      setLoading(false);
      return;
    }

    try {
      await onRolesUpdated(member.id, selectedRoles);
      onClose();
    } catch (error) {
      console.error('Failed to update member roles:', error);
      setError('Failed to update member roles. Please try again.');
      setLoading(false);
    }
  };

  const hasRoleChanged = () => {
    if (selectedRoles.length !== member.roles.length) return true;
    return !selectedRoles.every(role => member.roles.includes(role));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ backgroundColor: 'var(--bg-primary)' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="heading-secondary text-xl">Edit Member Roles</h2>
          <button
            onClick={onClose}
            className="modal-close-btn"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {error && (
            <div className="error-banner mb-6">
              <div className="text-error">{error}</div>
            </div>
          )}

          {/* Member Information */}
          <div className="form-group">
            <label className="form-label">Member</label>
            <div style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
              {member.displayName || member.email || 'Unknown User'}
              {member.email && member.displayName && (
                <span style={{ color: 'var(--text-secondary)', marginLeft: '8px', fontSize: '13px' }}>
                  ({member.email})
                </span>
              )}
            </div>
          </div>

          {/* Role Selection */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label className="form-label">Assign Role</label>
            <p className="text-muted text-sm" style={{ marginBottom: '0.75rem' }}>
              Select a role for this member.
            </p>

            <div className="roles-list">
              {availableRoles.map(({ role, label }) => (
                <div key={role} className="role-option">
                  <label className="role-checkbox">
                    <input
                      type="radio"
                      name="role"
                      checked={selectedRoles.includes(role)}
                      onChange={() => setSelectedRoles([role])}
                      className="checkbox-input"
                    />
                    <div className="role-info">
                      <span className="font-semibold">{label}</span>
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost mr-3"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || selectedRoles.length === 0 || !hasRoleChanged()}
            >
              {loading ? 'Updating...' : 'Update Roles'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMemberRolesModal;
